import {createContext, FC, useCallback, useContext, useEffect, useRef, useState} from 'react';

import {ScraperItem} from '@shared/models/parser';
import {
  decompressScraperItemAt,
  type CompressedScraperItems,
} from '@shared/scraper_item_compression';
import {deserializeCompressedScraperItems} from '@shared/scraper_item_serialization';

import {apiCall} from '@website_frontend/lib/api';
import {
  deleteFromIndexedDB,
  getCurrentDbKey,
  getFromIndexedDB,
  saveCurrentDbKey,
  setInIndexedDB,
} from '@website_frontend/lib/db_cache_storage';

// Helper to lazily decompress a gzipped description
// Returns a Promise that resolves to the decompressed string
export async function decompressDescription(gzipped: Uint8Array): Promise<string> {
  if (gzipped.length === 0) {
    return '';
  }
  const decompressionStream = new DecompressionStream('gzip');
  // Create a copy to ensure we have a proper ArrayBuffer
  const buffer = gzipped.buffer.slice(
    gzipped.byteOffset,
    gzipped.byteOffset + gzipped.byteLength
  ) as ArrayBuffer;
  const compressedStream = new Blob([buffer]).stream();
  const decompressedStream = compressedStream.pipeThrough(decompressionStream);
  const decompressedBlob = await new Response(decompressedStream).blob();
  const decompressedBuffer = await decompressedBlob.arrayBuffer();
  return new TextDecoder().decode(decompressedBuffer);
}

// Decompress gzipped ArrayBuffer, deserialize compressed format, and decompress all items
async function decompressAndParse(buffer: ArrayBuffer): Promise<CompressedScraperItems> {
  // First, decompress the gzip (the file is gzipped when stored in S3)
  const decompressionStream = new DecompressionStream('gzip');
  const compressedStream = new Blob([buffer]).stream();
  const decompressedStream = compressedStream.pipeThrough(decompressionStream);
  const decompressedBlob = await new Response(decompressedStream).blob();
  const decompressedBuffer = await decompressedBlob.arrayBuffer();

  // Deserialize the compressed format
  return deserializeCompressedScraperItems(decompressedBuffer);
}

export type DbDataState =
  | {type: 'idle'}
  | {type: 'checking_cache'}
  | {type: 'fetching_urls'}
  | {type: 'loading_from_cache'}
  | {type: 'fetching_parts'; progress?: number; total?: number}
  | {type: 'reassembling'}
  | {type: 'saving_cache'}
  | {type: 'decompressing'}
  | {type: 'success'}
  | {type: 'error'; error: Error};

type DbDataContextValue = {
  state: DbDataState;
  data: CompressedScraperItemsApi | undefined;
  reload: () => Promise<void>;
};

const DbDataContext = createContext<DbDataContextValue | undefined>(undefined);

export const DbDataProvider: FC<{children: React.ReactNode}> = ({children}) => {
  const [state, setState] = useState<DbDataState>({type: 'idle'});
  const [data, setData] = useState<CompressedScraperItemsApi | undefined>(undefined);
  const cacheKeyRef = useRef<string | undefined>(undefined);

  const maybeSetState = useCallback((state: DbDataState, opts?: {force?: boolean}) => {
    const {force = false} = opts ?? {};
    setState(prevState => {
      if (prevState.type === 'success' && !force) {
        return prevState;
      }
      return state;
    });
  }, []);

  const loadFromCache = useCallback(async (): Promise<void> => {
    try {
      maybeSetState({type: 'checking_cache'});
      cacheKeyRef.current = await getCurrentDbKey();
      const cachedKey = cacheKeyRef.current;
      if (!cachedKey) {
        return;
      }
      maybeSetState({type: 'loading_from_cache'});
      const cachedBuffer = await getFromIndexedDB(cachedKey);
      if (!cachedBuffer) {
        return;
      }
      maybeSetState({type: 'decompressing'});
      const parsedItems = await decompressAndParse(cachedBuffer);
      setData(createCompressedScraperItemsApi(parsedItems));
      maybeSetState({type: 'success'});
    } catch (error) {
      console.error('Error loading from cache:', error);
    }
  }, [maybeSetState]);

  const loadDbData = useCallback(async () => {
    try {
      const cachedKey = cacheKeyRef.current;

      maybeSetState({type: 'fetching_urls'});
      const {key, parts} = await apiCall('POST /get-db-urls', {current: cachedKey});

      // If no parts (meaning current matched latest), we can stop here
      if (!parts || parts.length === 0) {
        return;
      }

      // Fetch parts as raw compressed bytes (Content-Encoding override prevents decompression)
      // Note: Partial gzip streams can't be decompressed individually, so we reassemble first
      maybeSetState({type: 'fetching_parts', progress: 0, total: parts.length});
      const compressedParts = await Promise.all(
        parts.map(async (part, index) => {
          const response = await fetch(part.url, {
            headers: {
              Range: `bytes=${part.startByte}-${part.endByte}`,
            },
          });
          // Get raw compressed bytes (no automatic decompression)
          const result = new Uint8Array(await response.arrayBuffer());
          maybeSetState({type: 'fetching_parts', progress: index + 1, total: parts.length});
          return result;
        })
      );

      // Reassemble compressed parts in order
      maybeSetState({type: 'reassembling'});
      const totalLength = compressedParts.reduce((sum, part) => sum + part.length, 0);
      const gzipped = new Uint8Array(totalLength);
      let offset = 0;
      for (const part of compressedParts) {
        gzipped.set(part, offset);
        offset += part.length;
      }

      // Save to IndexedDB (store ArrayBuffer directly)
      maybeSetState({type: 'saving_cache'});
      await saveCurrentDbKey(key);
      await setInIndexedDB(key, gzipped.buffer);

      // Clean up old cache if different key
      if (cachedKey && cachedKey !== key) {
        await deleteFromIndexedDB(cachedKey);
      }

      // Decompress and parse
      maybeSetState({type: 'decompressing'});
      const parsedItems = await decompressAndParse(gzipped.buffer);
      setData(createCompressedScraperItemsApi(parsedItems));
      maybeSetState({type: 'success'});
    } catch (error) {
      console.error('Error loading from server:', error);
      maybeSetState({
        type: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }, [maybeSetState]);

  useEffect(() => {
    (async () => {
      await loadFromCache();
      await loadDbData();
    })().catch(error => {
      console.error('Error loading from cache:', error);
    });
  }, [loadFromCache, loadDbData]);

  const value: DbDataContextValue = {state, data, reload: loadDbData};
  return <DbDataContext.Provider value={value}>{children}</DbDataContext.Provider>;
};

export function useDbData(): {
  state: DbDataState;
  data: CompressedScraperItemsApi | undefined;
  reload: () => Promise<void>;
} {
  const context = useContext(DbDataContext);
  if (context === undefined) {
    throw new Error('useDbData must be used within DbDataProvider');
  }
  // Note: opts.autoLoad is ignored here since it's handled by the Provider
  return context;
}

export interface CompressedScraperItemsApi {
  raw: CompressedScraperItems;
  length: number;
  get: (id: number) => ScraperItem | undefined;
  filter: (fn: (item: ScraperItem) => boolean, opts?: {limit?: number}) => number[];
  forEach: (fn: (item: ScraperItem) => void) => void;
}

function createCompressedScraperItemsApi(
  compressed: CompressedScraperItems
): CompressedScraperItemsApi {
  const indexById = new Map<number, number>();
  for (let index = 0; index < compressed.length; index++) {
    const id = compressed.id[index];
    if (id) {
      indexById.set(id, index);
    }
  }
  return {
    raw: compressed,
    length: compressed.length,
    get: id => {
      const index = indexById.get(id);
      if (index === undefined) {
        return undefined;
      }
      return decompressScraperItemAt(compressed, index);
    },
    filter: (fn, opts) => {
      const ids: number[] = [];
      let count = 0;
      for (let i = 0; i < compressed.length; i++) {
        const item = decompressScraperItemAt(compressed, i);
        if (fn(item)) {
          ids.push(item.id);
          count++;
          if (opts?.limit && count >= opts.limit) {
            break;
          }
        }
      }
      return ids;
    },
    forEach: fn => {
      for (let i = 0; i < compressed.length; i++) {
        const item = decompressScraperItemAt(compressed, i);
        fn(item);
      }
    },
  };
}
